import { type ChangeEvent, memo, useMemo, useState } from 'react';

import { TextInput } from 'cpool-components/input/TextInput';
import DOMPurify from 'dompurify';
import parse, { domToReact, Element } from 'html-react-parser';
import type { DOMNode, HTMLReactParserOptions } from 'html-react-parser';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';

import type { EmailComposerForm } from './schemas/emailComposerForm.schema';

type Props = {
    content: string;
    getValues: UseFormGetValues<EmailComposerForm>;
    revision: number;
    setValue: UseFormSetValue<EmailComposerForm>;
};

type FreetextFieldName = `freetext.${'body' | 'offers'}.${number}.value`;

const isFreetextFieldName = (name: string): name is FreetextFieldName =>
    /^freetext\.(body|offers)\.\d+\.value$/.test(name);

const isDisabledAttribute = (disabled?: string) => disabled === '' || disabled === 'disabled' || disabled === 'true';

const containsFreetextInput = (node: Element): boolean =>
    node.children.some((child) => {
        if (!(child instanceof Element)) return false;
        if (child.name === 'input' && isFreetextFieldName(child.attribs.name ?? '')) return true;
        return containsFreetextInput(child);
    });

type HtmlMailFreetextInputProps = {
    disabled: boolean;
    initialValue: string;
    name: FreetextFieldName;
    placeholder?: string;
    setValue: UseFormSetValue<EmailComposerForm>;
};

const HtmlMailFreetextInput: React.FC<HtmlMailFreetextInputProps> = ({
    disabled,
    initialValue,
    name,
    placeholder,
    setValue,
}) => {
    const [inputValue, setInputValue] = useState(initialValue);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
        setValue(name, event.target.value, { shouldDirty: true });
    };

    return (
        <TextInput
            id={name}
            className="my-2 w-full"
            disabled={disabled}
            name={name}
            onChange={handleChange}
            placeholder={placeholder}
            value={inputValue}
        />
    );
};

/**
 * Renders the sanitized HTML mail preview as a React tree.
 *
 * html-react-parser turns the template HTML into elements and swaps raw freetext <input> tags for CPool TextInputs
 * that stay in the same place in the markup. Memoizing the parse on the content string keeps that tree stable so
 * getAcceptedOffers polls do not remount the inputs or jump the caret.
 */
export const HtmlMailPreview = memo(({ content, getValues, revision, setValue }: Props) => {
    const parsedContent = useMemo(() => {
        const sanitized = DOMPurify.sanitize(content, { ADD_TAGS: ['style'] });

        const options: HTMLReactParserOptions = {
            replace(domNode) {
                if (!(domNode instanceof Element)) return undefined;

                // TextInput renders a <div>, so a freetext field inside a <p> is invalid HTML once React owns the tree.
                if (domNode.name === 'p' && containsFreetextInput(domNode)) {
                    return <div>{domToReact(domNode.children as DOMNode[], options)}</div>;
                }

                if (domNode.name !== 'input') return undefined;

                const { disabled, name, placeholder } = domNode.attribs;
                if (!name || !isFreetextFieldName(name)) return undefined;

                return (
                    <span className="block">
                        <HtmlMailFreetextInput
                            key={`${name}-${revision}`}
                            disabled={isDisabledAttribute(disabled)}
                            initialValue={getValues(name) ?? ''}
                            name={name}
                            placeholder={placeholder}
                            setValue={setValue}
                        />
                    </span>
                );
            },
        };

        return parse(sanitized, options);
    }, [content, getValues, revision, setValue]);

    return <div>{parsedContent}</div>;
});

HtmlMailPreview.displayName = 'HtmlMailPreview';
